import * as React from 'react'
import clx from 'classnames'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { useTable } from '@/modules/drivers/context/TableContext'
import Checkbox from '@/modules/drivers/shared/Checkbox'
import { FaIcon, faClose, faIdBadge, faIdBadgeSolid } from '@/lib/fa'
import SearchColor from '../../../assets/icons_new/SearchColor.svg'
import Users from '../../../assets/icons_new/Users.svg'

import SearchInput from '@/modules/drivers/shared/SearchInput'
import GroupPicker from '@/modules/drivers/shared/DriverGroupPicker'
import { GroupModel } from '@/modules/drivers/types/groups'
import useDriverService from '@/modules/drivers/services/DriverService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  onSelectionChange: (selectedIds: number[]) => void
  companyId: string
  initialSelectedIds?: number[]
  favoriteDriverIds?: string[]
  onToggleFavorite?: (driverId: string) => void
}

const PrintoutDriversTable: React.FC<Props> = ({
  onSelectionChange,
  companyId,
  favoriteDriverIds,
  onToggleFavorite,
}) => {
  const { t } = useLocalize()
  const {
    basicDrivers: allBasicDrivers,
    search,
    setSearch,
    groups,
    setActualGroup,
    actualGroup,
    refresh,
    isLoadingBasicDrivers,
    isFetchingBasicDrivers,
  } = useDrivers()
  const { getDriversInGroup } = useDriverService()
  const {
    toggleAll,
    toggleId,
    resetAll,
    selectedGroup,
    setAllIds,
    retainIds,
    isSelectedAny,
    isActiveGroup,
  } = useTable()

  const [groupDrivers, setGroupDrivers] = React.useState<{
    groupId: number
    ids: number[]
  } | null>(null)
  const [wasSearchActive, setWasSearchActive] = React.useState(false)

  const actualGroupId = actualGroup?.id ?? 0

  React.useEffect(() => {
    if (actualGroupId <= 0) {
      setGroupDrivers(null)
      return undefined
    }

    let cancelled = false
    const fetchGroup = async () => {
      const ids = await getDriversInGroup({
        companyId,
        groupId: actualGroupId,
      })
      if (!cancelled) {
        setGroupDrivers({ groupId: actualGroupId, ids: ids || [] })
      }
    }
    void fetchGroup()
    return () => {
      cancelled = true
    }
  }, [actualGroupId, companyId])

  const isGroupLoading =
    actualGroupId > 0 && groupDrivers?.groupId !== actualGroupId

  const drivers = React.useMemo(() => {
    if (actualGroupId === 0) return allBasicDrivers
    if (actualGroupId === -1) return allBasicDrivers.filter((d) => d.isActive)
    if (actualGroupId === -2) return allBasicDrivers.filter((d) => !d.isActive)
    if (isGroupLoading) return []
    const ids = new Set(groupDrivers?.ids ?? [])
    return allBasicDrivers.filter((d) => ids.has(d.id) && d.isActive)
  }, [allBasicDrivers, actualGroupId, groupDrivers, isGroupLoading])

  const filteredDrivers = React.useMemo(() => {
    if (!search || !search.trim()) return drivers
    const q = search.toLowerCase()
    return drivers.filter((d) => (d.fullName ?? '').toLowerCase().includes(q))
  }, [drivers, search])

  const { sortedData, setSortConfig } = useSortableData({
    data: filteredDrivers,
    tableColumns: [],
  })

  const sortedDataWithSelected = React.useMemo(() => {
    let data: typeof sortedData

    if (
      search ||
      !wasSearchActive ||
      !selectedGroup?.ids ||
      selectedGroup.ids.length === 0
    ) {
      data = sortedData
    } else {
      const selected = sortedData.filter((driver) =>
        selectedGroup.ids.includes(driver.id),
      )
      const notSelected = sortedData.filter(
        (driver) => !selectedGroup.ids.includes(driver.id),
      )
      data = [...selected, ...notSelected]
    }

    if (favoriteDriverIds && favoriteDriverIds.length > 0) {
      const favoriteSet = new Set(favoriteDriverIds)
      const favorites = data.filter((driver) =>
        favoriteSet.has(String(driver.id)),
      )
      const rest = data.filter((driver) => !favoriteSet.has(String(driver.id)))
      return [...favorites, ...rest]
    }

    return data
  }, [
    sortedData,
    selectedGroup?.ids,
    search,
    wasSearchActive,
    favoriteDriverIds,
  ])

  React.useEffect(() => {
    if (!selectedGroup?.ids || selectedGroup.ids.length === 0) {
      setWasSearchActive(false)
    }
  }, [selectedGroup?.ids])

  React.useEffect(() => {
    setSortConfig({ key: 'surname', direction: 'asc' })
  }, [setSortConfig])

  React.useEffect(() => {
    setAllIds(
      filteredDrivers.map((driver) => ({
        id: driver.id,
        isActive: driver.isActive ?? true,
      })),
    )
  }, [filteredDrivers, setAllIds])

  React.useEffect(() => {
    if (
      isGroupLoading ||
      isLoadingBasicDrivers ||
      allBasicDrivers.length === 0
    ) {
      return
    }
    retainIds(drivers.map((driver) => driver.id))
  }, [
    drivers,
    isGroupLoading,
    isLoadingBasicDrivers,
    allBasicDrivers.length,
    retainIds,
    selectedGroup?.ids,
  ])

  const prevSelectionRef = React.useRef<number[]>([])
  const onSelectionChangeRef = React.useRef(onSelectionChange)

  React.useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange
  }, [onSelectionChange])

  React.useEffect(() => {
    const currentIds = selectedGroup?.ids || []
    const prevIds = prevSelectionRef.current

    const currentSet = new Set(currentIds)
    const prevSet = new Set(prevIds)

    if (
      currentIds.length !== prevIds.length ||
      !currentIds.every((id) => prevSet.has(id)) ||
      !prevIds.every((id) => currentSet.has(id))
    ) {
      prevSelectionRef.current = currentIds
      onSelectionChangeRef.current(currentIds)
    }
  }, [selectedGroup?.ids])

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text
    const regex = new RegExp(`(${query})`, 'gi')
    const parts = text.split(regex)
    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span key={i} className="tw-bg-[#FFF47B]">
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </>
    )
  }

  const [showSearch, setShowSearch] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (search && search.trim() !== '') {
      setWasSearchActive(true)
    }
  }, [search, selectedGroup?.ids])

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSearch('')
        setShowSearch(false)
      }
    }

    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const selectedIds = selectedGroup?.ids ?? []

  const bulkIsActive =
    isActiveGroup ?? sortedDataWithSelected[0]?.isActive ?? true
  const bulkRows = sortedDataWithSelected.filter(
    (d) => (d.isActive ?? true) === bulkIsActive,
  )
  const allVisibleChecked =
    bulkRows.length > 0 && bulkRows.every((d) => selectedIds.includes(d.id))
  const selectedVisibleCount = selectedIds.filter((id) =>
    sortedDataWithSelected.some((d) => d.id === id),
  ).length

  const handleSelectGroup = (option: GroupModel | null) => {
    if (!option || option.id === 0) {
      setActualGroup(null)
    } else if (option.id === -1) {
      setActualGroup({ id: -1, name: t('common.active') })
    } else if (option.id === -2) {
      setActualGroup({ id: -2, name: t('common.inactive') })
    } else {
      const group = groups.find((g) => g.id === option.id)
      if (group) setActualGroup(group)
    }
  }

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      <div
        ref={containerRef}
        className="tw-z-[70] tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-bg-white tw-pr-4 tw-shadow-sm"
      >
        <div className="tw-flex tw-w-fit tw-items-center tw-gap-2 tw-p-3">
          <div
            className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-p-1 ${showSearch ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'}`}
            onClick={() => setShowSearch((prev) => !prev)}
          >
            <img src={SearchColor} alt="Search Icon" className="tw-w-6" />
          </div>
          <div className="tw-absolute tw-top-10">
            {showSearch && (
              <SearchInput
                search={search}
                searchResults={filteredDrivers.length}
                setSearch={setSearch}
              />
            )}
          </div>
        </div>
        <div className="tw-z-[200] tw-w-full">
          <GroupPicker
            fullWidth
            companyId={companyId}
            refreshGroups={refresh}
            name={t('common.all')}
            options={groups.map((g) => ({ id: g.id, name: g.name }))}
            onSelect={handleSelectGroup}
            icon={Users}
            actualGroup={actualGroup}
          />
        </div>
      </div>

      <div className="custom-scrollbar tw-relative tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
        {isFetchingBasicDrivers && !isLoadingBasicDrivers && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-start tw-justify-center tw-bg-white/60 tw-pt-6">
            <LoaderIcon className="tw-animate-spin tw-text-primary" />
          </div>
        )}
        {isLoadingBasicDrivers ? (
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        ) : sortedDataWithSelected.length === 0 ? (
          <div className="tw-p-4 tw-text-center tw-text-xs tw-text-content-base">
            {!search ? t('printout.no-drivers') : t('printout.no-search-results')}
          </div>
        ) : (
          <ul className="tw-flex tw-flex-col">
            <li className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-base-200 tw-bg-content-primary tw-px-3 tw-py-2">
              <Checkbox
                checked={allVisibleChecked}
                onChange={(checked) => toggleAll(checked, bulkIsActive)}
              />
              <span className="tw-text-xs tw-font-medium tw-text-content-base">
                {selectedVisibleCount} / {sortedDataWithSelected.length}
              </span>

              <button
                type="button"
                disabled={selectedIds.length === 0}
                onClick={resetAll}
                title="Wyczyść zaznaczonych kierowców"
                className={`tw-ml-auto tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1 tw-rounded-md tw-px-2 tw-py-0.5 tw-text-xs tw-text-content-base tw-transition-colors hover:tw-bg-base-200 ${selectedIds.length === 0 ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
              >
                <FaIcon icon={faClose} className="tw-h-3 tw-w-3" />
                <span>Wyczyść</span>
              </button>
            </li>
            {sortedDataWithSelected.map((driver) => {
              const isChecked = selectedIds.includes(driver.id)
              const isActive = driver?.isActive ?? true
              const isDisabled =
                isSelectedAny &&
                isActiveGroup !== undefined &&
                isActiveGroup !== isActive
              const isFavorite = favoriteDriverIds?.includes(String(driver.id))

              return (
                <li
                  key={driver.id}
                  onClick={() => !isDisabled && toggleId(driver.id, isActive)}
                  className={clx(
                    'tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-b tw-border-base-300/50 tw-px-3 tw-py-2 tw-text-sm tw-transition-colors hover:tw-bg-primary/10',
                    isChecked && 'tw-bg-primary/10 tw-font-medium',
                    !isActive && 'tw-opacity-50',
                    isDisabled && 'tw-cursor-not-allowed tw-opacity-30',
                  )}
                >
                  <div
                    className="tw--m-2 tw-grid tw-place-items-center tw-p-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!isDisabled) toggleId(driver.id, isActive)
                    }}
                  >
                    <Checkbox
                      disabled={isDisabled}
                      checked={isChecked}
                      onChange={() => toggleId(driver.id, isActive)}
                    />
                  </div>
                  <FaIcon
                    icon={isChecked ? faIdBadgeSolid : faIdBadge}
                    className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-content-base"
                  />
                  <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-content-base">
                    {highlightMatch(driver.fullName ?? '', search)}
                  </span>
                  {/* Favorites star — only when a toggle handler is provided
                      (i.e. the IMI export pages). */}
                  {onToggleFavorite && (
                    <button
                      type="button"
                      title={
                        isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'
                      }
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleFavorite(String(driver.id))
                      }}
                      className={`tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded tw-text-base tw-leading-none hover:tw-bg-base-300 ${isFavorite ? 'tw-text-yellow-500' : 'tw-text-content-base tw-text-opacity-40'}`}
                    >
                      {isFavorite ? '★' : '☆'}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export default PrintoutDriversTable
